import React, { useEffect, useMemo, useRef, useState } from 'react';

const TZ = 'America/Martinique';
const LOG_URL = '/api/agent/broadcast-log';
const SHOWS_URL = '/api/shows';
/** Un programme peut avoir commencé bien avant le début de la fenêtre affichée. */
const LOOKBEHIND_MS = 4 * 3600 * 1000;
const REFRESH_MS = 30 * 1000;
const PROGRAM_MIN_SECONDS = 120;

const WINDOWS = [
  { label: '1 h', ms: 3600 * 1000, tickMs: 10 * 60 * 1000 },
  { label: '3 h', ms: 3 * 3600 * 1000, tickMs: 30 * 60 * 1000 },
  { label: '6 h', ms: 6 * 3600 * 1000, tickMs: 3600 * 1000 },
  { label: '12 h', ms: 12 * 3600 * 1000, tickMs: 2 * 3600 * 1000 },
  { label: '24 h', ms: 24 * 3600 * 1000, tickMs: 3 * 3600 * 1000 },
];

interface Airing {
  uid: string;
  name: string;
  show: string | null;
  episode: number | null;
  startedAt: string;
  endedAt: string | null;
  lastSeenAt: string;
  duration: number | null;
}

interface Entry {
  key: string;
  start: number;
  end: number;
  onAir: boolean;
  program: boolean;
  short: string;
  title: string;
}

function formatClock(ms: number, withSeconds = false): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  }).format(new Date(ms));
}

function formatDay(ms: number): string {
  return new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(ms));
}

function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
}

function martiniqueOffsetMs(ms: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - Math.floor(ms / 1000) * 1000;
}

/** « ACT », « KS_12h20_Sam » ou « DDS_13h05_Sam » → sigle connu du catalogue. */
function guessAbbr(item: Airing, titles: Record<string, string>): string | null {
  if (item.show) return item.show;
  const head = item.name.split(/[_\s-]/)[0]?.toUpperCase();
  return head && titles[head] ? head : null;
}

function toEntry(item: Airing, titles: Record<string, string>, now: number): Entry {
  const start = Date.parse(item.startedAt);
  const lastSeen = Date.parse(item.lastSeenAt);
  const planned = start + (item.duration || 0) * 1000;
  const onAir = !item.endedAt && now - lastSeen < 2 * 60 * 1000;
  const end = item.endedAt ? Date.parse(item.endedAt) : onAir ? Math.min(planned, now) : Math.max(start, Math.min(planned, lastSeen));
  const abbr = guessAbbr(item, titles);
  const program = !!abbr || (item.duration || 0) >= PROGRAM_MIN_SECONDS;
  const title = abbr && titles[abbr] ? titles[abbr] : item.name;
  return {
    key: `${item.uid}-${item.startedAt}`,
    start,
    end: Math.max(end, start + 1000),
    onAir,
    program,
    short: abbr || item.name,
    title: item.episode != null && abbr ? `${title} · n° ${item.episode}` : title,
  };
}

interface GuideProps {
  rangeStart: number;
  rangeEnd: number;
  positionMs: number | null;
  onSeek: (ms: number) => void;
  onPlayProgram: (startMs: number) => void;
}

const LiveRecordGuide: React.FC<GuideProps> = ({ rangeStart, rangeEnd, positionMs, onSeek, onPlayProgram }) => {
  const [windowIndex, setWindowIndex] = useState(1);
  const [pinnedEnd, setPinnedEnd] = useState<number | null>(null);
  const [airings, setAirings] = useState<Airing[]>([]);
  const [logError, setLogError] = useState<string | null>(null);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [showFillers, setShowFillers] = useState(false);
  const [hoverMs, setHoverMs] = useState<number | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const span = WINDOWS[windowIndex];
  const winEnd = pinnedEnd == null ? rangeEnd : Math.min(pinnedEnd, rangeEnd);
  const winStart = Math.max(rangeStart, winEnd - span.ms);

  // La fenêtre suit la lecture : si la position en sort, on la recadre autour.
  useEffect(() => {
    if (positionMs == null) return;
    if (positionMs >= winStart && positionMs <= winEnd) return;
    const end = positionMs + span.ms * 0.25;
    setPinnedEnd(end >= rangeEnd ? null : end);
  }, [positionMs, winStart, winEnd, span.ms, rangeEnd]);

  useEffect(() => {
    let cancelled = false;
    fetch(SHOWS_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body?.shows) return;
        const map: Record<string, string> = {};
        for (const s of body.shows) if (s.abbreviation && s.title) map[String(s.abbreviation).toUpperCase()] = s.title;
        setTitles(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const following = pinnedEnd == null;
  const fetchFrom = Math.floor((winStart - LOOKBEHIND_MS) / 60000) * 60000;
  const fetchTo = following ? null : Math.ceil(winEnd / 60000) * 60000;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const params = new URLSearchParams({ from: new Date(fetchFrom).toISOString() });
      if (fetchTo != null) params.set('to', new Date(fetchTo).toISOString());
      try {
        const res = await fetch(`${LOG_URL}?${params}`, { cache: 'no-store' });
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok || !body?.success) {
          setLogError(body?.message || 'Journal de diffusion indisponible.');
          return;
        }
        setAirings(body.data.items as Airing[]);
        setLogError(null);
      } catch {
        if (!cancelled) setLogError('Journal de diffusion indisponible.');
      }
    };
    load();
    if (fetchTo != null) return () => { cancelled = true; };
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fetchFrom, fetchTo]);

  const entries = useMemo(() => {
    const now = Date.now();
    return airings
      .map((a) => toEntry(a, titles, now))
      .filter((e) => e.end > winStart && e.start < winEnd)
      .sort((a, b) => b.start - a.start);
    // rangeEnd avance avec le direct : il sert à rafraîchir l'élément à l'antenne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airings, titles, winStart, winEnd, rangeEnd]);

  const current = positionMs == null ? null : entries.find((e) => e.program && positionMs >= e.start && positionMs < e.end) || null;

  const ticks = useMemo(() => {
    const offset = martiniqueOffsetMs(winStart);
    const out: number[] = [];
    let t = Math.ceil((winStart + offset) / span.tickMs) * span.tickMs - offset;
    for (; t <= winEnd; t += span.tickMs) out.push(t);
    return out;
  }, [winStart, winEnd, span.tickMs]);

  const total = Math.max(1, winEnd - winStart);
  const pct = (ms: number) => `${((Math.min(Math.max(ms, winStart), winEnd) - winStart) / total) * 100}%`;
  const widthPct = (a: number, b: number) => `${((Math.min(b, winEnd) - Math.max(a, winStart)) / total) * 100}%`;

  const msAtPointer = (clientX: number): number | null => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return null;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return winStart + ratio * total;
  };

  const page = (direction: -1 | 1) => {
    const end = winEnd + direction * span.ms * 0.5;
    if (end >= rangeEnd) setPinnedEnd(null);
    else setPinnedEnd(Math.max(end, rangeStart + span.ms));
  };

  const programs = entries.filter((e) => e.program);
  const fillers = entries.filter((e) => !e.program);
  const rows = showFillers ? entries : programs;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: 14, marginRight: 6 }}>Grille diffusée</span>
        <button type="button" style={chipBtn} onClick={() => page(-1)} disabled={winStart <= rangeStart} title="Plus tôt">◀</button>
        <button type="button" style={chipBtn} onClick={() => page(1)} disabled={following} title="Plus tard">▶</button>
        {WINDOWS.map((w, i) => (
          <button
            key={w.label}
            type="button"
            style={i === windowIndex ? { ...chipBtn, background: '#334155', color: '#f8fafc' } : chipBtn}
            onClick={() => setWindowIndex(i)}
          >
            {w.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 12 }}>
          {formatDay(winStart)} {formatClock(winStart)} → {formatDay(winEnd) !== formatDay(winStart) ? `${formatDay(winEnd)} ` : ''}{formatClock(winEnd)}
        </span>
      </div>

      <div
        ref={barRef}
        role="slider"
        aria-label="Position dans l’enregistrement"
        aria-valuemin={winStart}
        aria-valuemax={winEnd}
        aria-valuenow={positionMs ?? winEnd}
        tabIndex={0}
        onMouseMove={(e) => setHoverMs(msAtPointer(e.clientX))}
        onMouseLeave={() => setHoverMs(null)}
        onClick={(e) => {
          const ms = msAtPointer(e.clientX);
          if (ms != null) onSeek(ms);
        }}
        style={{
          position: 'relative', height: 46, background: '#0f1113', border: '1px solid #23262a',
          borderRadius: 8, cursor: 'pointer', overflow: 'hidden', userSelect: 'none',
        }}
      >
        {fillers.map((e) => (
          <div
            key={e.key}
            title={`${formatClock(e.start, true)} · ${e.title}`}
            style={{ position: 'absolute', top: 30, bottom: 4, left: pct(e.start), width: widthPct(e.start, e.end), minWidth: 2, background: '#3f4650', borderRadius: 2 }}
          />
        ))}
        {programs.map((e) => {
          const isCurrent = current?.key === e.key;
          return (
            <div
              key={e.key}
              title={`${formatClock(e.start, true)} · ${e.title}`}
              onClick={(ev) => {
                ev.stopPropagation();
                onPlayProgram(e.start);
              }}
              style={{
                position: 'absolute', top: 4, height: 22, left: pct(e.start), width: widthPct(e.start, e.end), minWidth: 3,
                background: isCurrent ? '#1d4ed8' : '#1e3a5f', borderLeft: '3px solid #60a5fa', borderRadius: 4,
                color: '#e2e8f0', fontSize: 11, fontWeight: 700, lineHeight: '22px', paddingLeft: 4,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box',
              }}
            >
              {e.short}
            </div>
          );
        })}
        {positionMs != null && positionMs >= winStart && positionMs <= winEnd && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(positionMs), width: 2, marginLeft: -1, background: '#f59e0b', pointerEvents: 'none' }} />
        )}
        {hoverMs != null && (
          <>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(hoverMs), width: 1, background: 'rgba(248,250,252,0.45)', pointerEvents: 'none' }} />
            <div style={{
              position: 'absolute', bottom: 2, left: pct(hoverMs), transform: 'translateX(-50%)', pointerEvents: 'none',
              background: '#f8fafc', color: '#0f1113', fontSize: 10, fontWeight: 700, padding: '0 4px', borderRadius: 3,
            }}>
              {formatClock(hoverMs, true)}
            </div>
          </>
        )}
      </div>
      <div style={{ position: 'relative', height: 16, marginTop: 2 }}>
        {ticks.map((t) => (
          <span key={t} style={{ position: 'absolute', left: pct(t), transform: 'translateX(-50%)', color: '#64748b', fontSize: 10 }}>
            {formatClock(t)}
          </span>
        ))}
      </div>

      {logError && <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 12 }}>{logError}</p>}

      <div style={{ display: 'flex', alignItems: 'center', marginTop: 10, marginBottom: 6 }}>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>
          {programs.length} programme{programs.length > 1 ? 's' : ''} sur la fenêtre
        </span>
        <label style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showFillers} onChange={(e) => setShowFillers(e.target.checked)} />
          Afficher pubs et habillage
        </label>
      </div>

      <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #23262a', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#1c1f23', color: '#94a3b8', textAlign: 'left' }}>
              <th style={th}>Début</th>
              <th style={th}>Programme</th>
              <th style={th}>Durée</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, color: '#64748b' }}>Aucun élément diffusé sur cette fenêtre.</td></tr>
            )}
            {rows.map((e) => {
              const isCurrent = current?.key === e.key;
              const playable = e.start >= rangeStart;
              return (
                <tr
                  key={e.key}
                  onClick={() => playable && onPlayProgram(e.start)}
                  style={{
                    cursor: playable ? 'pointer' : 'default',
                    background: isCurrent ? 'rgba(29,78,216,0.28)' : undefined,
                    color: e.program ? '#e2e8f0' : '#94a3b8',
                    opacity: playable ? 1 : 0.5,
                  }}
                >
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDay(e.start) !== formatDay(Date.now()) && <span style={{ color: '#64748b' }}>{formatDay(e.start)} </span>}
                    {formatClock(e.start, true)}
                  </td>
                  <td style={td}>
                    <span style={{ fontWeight: e.program ? 700 : 400 }}>{e.title}</span>
                    {e.program && e.short !== e.title && <span style={{ color: '#64748b', marginLeft: 6 }}>{e.short}</span>}
                    {e.onAir && <span style={onAirBadge}>À L’ANTENNE</span>}
                    {isCurrent && <span style={playingBadge}>EN LECTURE</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: '#94a3b8' }}>{formatDuration(e.end - e.start)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#60a5fa', whiteSpace: 'nowrap' }}>{playable ? 'Lire ▶' : 'Hors archive'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const chipBtn: React.CSSProperties = {
  background: '#23262a',
  color: '#cbd5e1',
  border: '1px solid #353a40',
  borderRadius: 6,
  padding: '4px 10px',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

const th: React.CSSProperties = { padding: '7px 10px', fontWeight: 600, fontSize: 12, borderBottom: '1px solid #23262a' };
const td: React.CSSProperties = { padding: '7px 10px', borderBottom: '1px solid #1c1f23' };
const onAirBadge: React.CSSProperties = {
  marginLeft: 8, background: '#ef4444', color: '#fff', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 800,
};
const playingBadge: React.CSSProperties = {
  marginLeft: 8, background: '#f59e0b', color: '#121212', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 800,
};

export default LiveRecordGuide;
